import { useApiUrl, useCustom } from "@refinedev/core";
import { AutoComplete, Button, Card, Input, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React from "react";
import { ExportButtons } from "./components/ExportButtons";
import { FilterBar } from "./components/FilterBar";
import { SummaryCards } from "./components/SummaryCards";
import { VesselConsumptionTable } from "./components/VesselConsumptionTable";
import { VesselDetailDrawer } from "./components/VesselDetailDrawer";
import { ConsumptionPctCell } from "./components/ConsumptionPctCell";
import { exportScopeText } from "./lib/export-scope";
import { formatPeriodLabel, Period, periodQueryParams } from "./lib/period";
import {
  FleetSummary,
  Paginated,
  ShipcoConsumption,
  VesselConsumption,
} from "./lib/types";

const SHIPCO_PAGE_LIMIT = 25;

const money = (v: number) =>
  v.toLocaleString("en-US", { maximumFractionDigits: 2 });

// The shipco opened in the Shipcos table: the export sends it, and the note
// beside the export buttons names it.
type SelectedShipco = Pick<ShipcoConsumption, "shipcoId" | "shipcoName">;

interface ShipcoVesselsProps {
  shipcoId: number;
  period: Period;
  onVesselClick: (vessel: VesselConsumption) => void;
}

// Mounted per expanded shipco row - fetches that fleet lazily rather than
// pulling every shipco's vessels up front.
const ShipcoVessels: React.FC<ShipcoVesselsProps> = ({
  shipcoId,
  period,
  onVesselClick,
}) => {
  const apiUrl = useApiUrl();
  const {
    result: vesselsResult,
    query: { isLoading },
  } = useCustom<VesselConsumption[]>({
    url: `${apiUrl}/flat-fee-analytics/shipcos/${shipcoId}/vessels`,
    method: "get",
    config: { query: periodQueryParams(period) },
  });

  // useCustom's pre-load value is a frozen EMPTY_OBJECT ({}), which is
  // truthy, so `vesselsResult?.data ?? []` never falls back for this
  // bare-array endpoint. Guard explicitly (same pattern as
  // src/pages/bulk-email-jobs/show.tsx) or antd Table throws
  // "rawData.some is not a function" on first expand.
  const vessels = Array.isArray(vesselsResult?.data) ? vesselsResult.data : [];

  return (
    <VesselConsumptionTable
      vessels={vessels}
      loading={isLoading}
      onVesselClick={onVesselClick}
    />
  );
};

export const FlatFeeAnalyticsPage: React.FC = () => {
  const [period, setPeriod] = React.useState<Period>({
    mode: "year",
    year: dayjs().year(),
  });
  const [services, setServices] = React.useState<string[]>(["AVCS"]);
  const [search, setSearch] = React.useState("");
  const [typedSearch, setTypedSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [selectedShipco, setSelectedShipco] = React.useState<SelectedShipco | null>(
    null
  );
  const [drawerVessel, setDrawerVessel] = React.useState<VesselConsumption | null>(
    null
  );
  const [thresholdFilter, setThresholdFilter] = React.useState<number | null>(
    null
  );

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(typedSearch), 300);
    return () => clearTimeout(timer);
  }, [typedSearch]);

  const apiUrl = useApiUrl();

  const {
    result: summaryResult,
    query: { isFetching: summaryFetching },
  } = useCustom<FleetSummary>({
    url: `${apiUrl}/flat-fee-analytics/summary`,
    method: "get",
    config: { query: periodQueryParams(period) },
    queryOptions: { retry: 1 },
  });

  const {
    result: shipcosResult,
    query: { isFetching: shipcosFetching },
  } = useCustom<Paginated<ShipcoConsumption>>({
    url: `${apiUrl}/flat-fee-analytics/shipcos`,
    method: "get",
    config: {
      query: {
        ...periodQueryParams(period),
        search: search || undefined,
        page,
        limit: SHIPCO_PAGE_LIMIT,
      },
    },
  });

  const shipcos = shipcosResult?.data;

  const { result: shipcoSuggestionsResult } = useCustom<
    Paginated<ShipcoConsumption>
  >({
    url: `${apiUrl}/flat-fee-analytics/shipcos`,
    method: "get",
    config: {
      query: { ...periodQueryParams(period), search: debouncedSearch, page: 1, limit: 10 },
    },
    queryOptions: { enabled: debouncedSearch.length > 0 },
  });

  // Threshold-card drill-down: fetched only while a card filter is active.
  const {
    result: thresholdVesselsResult,
    query: { isFetching: thresholdVesselsFetching },
  } = useCustom<VesselConsumption[]>({
    url: `${apiUrl}/flat-fee-analytics/vessels`,
    method: "get",
    config: {
      query: {
        ...periodQueryParams(period),
        minPct: thresholdFilter ?? undefined,
      },
    },
    queryOptions: { enabled: thresholdFilter !== null },
  });
  const thresholdVessels = Array.isArray(thresholdVesselsResult?.data)
    ? thresholdVesselsResult.data
    : [];

  // Same EMPTY_OBJECT trap as ShipcoVessels above: useCustom's pre-load
  // result is a frozen truthy {}, so guard the nested shape before mapping.
  const shipcoSuggestions = Array.isArray(shipcoSuggestionsResult?.data?.data)
    ? shipcoSuggestionsResult.data.data
    : [];
  const shipcoOptions = shipcoSuggestions.map((s) => ({
    value: s.shipcoName,
  }));

  const handlePeriodChange = (next: Period) => {
    setPeriod(next);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // Toggle: clicking the active card clears the filter. Year/range and the
  // other filters stay in place and the drill-down refetches under them.
  // The card's vessel list replaces the Shipcos table, so the shipco opened
  // there no longer scopes the export: it goes back to the whole fleet, and
  // clearing the card does not bring that shipco back (ruling 2026-10-30).
  const handleThresholdClick = (threshold: number) => {
    setThresholdFilter((prev) => (prev === threshold ? null : threshold));
    setSelectedShipco(null);
  };

  const shipcoColumns: ColumnsType<ShipcoConsumption> = [
    {
      title: "Shipco",
      dataIndex: "shipcoName",
      key: "shipcoName",
    },
    {
      title: "Vessels",
      key: "vessels",
      render: (_, record) => (
        <>
          {record.vesselCount}
          {record.vesselsPendingBudget > 0
            ? ` (${record.vesselsPendingBudget} pending)`
            : ""}
        </>
      ),
    },
    {
      title: "Budget",
      dataIndex: "budget",
      key: "budget",
      render: (value) => money(value),
    },
    {
      title: "Spend",
      dataIndex: "spend",
      key: "spend",
      render: (value) => money(value),
    },
    {
      title: "Consumption %",
      key: "consumptionPct",
      render: (_, record) => (
        <ConsumptionPctCell pct={record.consumptionPct} budgetPending={false} />
      ),
    },
    {
      title: "",
      key: "tags",
      render: (_, record) =>
        record.currencyMismatch ? (
          <Tag color="warning">currency mismatch</Tag>
        ) : null,
    },
  ];

  return (
    <>
      <Space style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <ExportButtons
          scope={{
            scopeType: selectedShipco ? "shipco" : "all",
            scopeId: selectedShipco?.shipcoId,
          }}
          period={period}
          scopeNote={exportScopeText(period, selectedShipco?.shipcoName ?? null)}
        />
      </Space>

      <div style={{ marginBottom: 16 }}>
        <FilterBar
          period={period}
          onPeriodChange={handlePeriodChange}
          services={services}
          onServicesChange={setServices}
        />
      </div>

      <SummaryCards
        summary={summaryResult?.data}
        loading={summaryFetching}
        activeThreshold={thresholdFilter}
        onThresholdClick={handleThresholdClick}
      />

      {thresholdFilter !== null ? (
        <Card
          title={`Vessels >= ${thresholdFilter}% (${formatPeriodLabel(period)})`}
          extra={
            <Button size="small" onClick={() => setThresholdFilter(null)}>
              Clear filter
            </Button>
          }
          style={{ marginTop: 16 }}
        >
          <VesselConsumptionTable
            vessels={thresholdVessels}
            loading={thresholdVesselsFetching}
            onVesselClick={setDrawerVessel}
          />
        </Card>
      ) : (
        <Card title="Shipcos" style={{ marginTop: 16 }}>
          <AutoComplete
            options={shipcoOptions}
            filterOption={false}
            value={typedSearch}
            style={{ maxWidth: 320, marginBottom: 16 }}
            onChange={(value) => {
              setTypedSearch(value);
              if (!value) handleSearch("");
            }}
            onSelect={(value) => {
              setTypedSearch(value);
              handleSearch(value);
            }}
          >
            <Input.Search
              allowClear
              placeholder="Search shipco"
              onSearch={handleSearch}
            />
          </AutoComplete>
          <Table<ShipcoConsumption>
            rowKey="shipcoId"
            loading={shipcosFetching}
            dataSource={shipcos?.data ?? []}
            columns={shipcoColumns}
            pagination={{
              current: shipcos?.page ?? page,
              pageSize: SHIPCO_PAGE_LIMIT,
              total: shipcos?.total ?? 0,
              onChange: (p) => setPage(p),
            }}
            rowClassName={(record) =>
              record.shipcoId === selectedShipco?.shipcoId
                ? "ant-table-row-selected"
                : ""
            }
            expandable={{
              onExpand: (expanded, record) => {
                if (expanded) {
                  setSelectedShipco({
                    shipcoId: record.shipcoId,
                    shipcoName: record.shipcoName,
                  });
                } else if (selectedShipco?.shipcoId === record.shipcoId) {
                  setSelectedShipco(null);
                }
              },
              expandedRowRender: (record) => (
                <ShipcoVessels
                  shipcoId={record.shipcoId}
                  period={period}
                  onVesselClick={setDrawerVessel}
                />
              ),
            }}
          />
        </Card>
      )}

      <VesselDetailDrawer
        vessel={drawerVessel}
        period={period}
        onClose={() => setDrawerVessel(null)}
      />
    </>
  );
};
